import { BotonExportar } from '@/components/dominio/BotonExportar';
import { ComentarioSeccion } from '@/components/dominio/ComentarioSeccion';
import { DataGrid } from '@/components/dominio/DataGrid';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { NOMBRES_MESES, SelectorPeriodo } from '@/components/dominio/SelectorPeriodo';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePeriodo } from '@/hooks/usePeriodo';
import { useNeta } from '@/hooks/useUtilidades';
import { cn } from '@/lib/utils';
import type { FilaEvolucionNeta } from '@adelante/shared';
import { formatearPeriodo, mesAnclaPeriodo } from '@adelante/shared';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { useRef } from 'react';
import {
  Bar,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const MESES_CORTOS = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

function abreviarCRC(valor: number): string {
  if (Math.abs(valor) >= 1e9) return `₡${(valor / 1e9).toFixed(1)}bn`;
  if (Math.abs(valor) >= 1e6) return `₡${(valor / 1e6).toFixed(0)}M`;
  return `₡${valor.toLocaleString('es-CR')}`;
}

const col = createColumnHelper<FilaEvolucionNeta>();

const COLUMNAS: ColumnDef<FilaEvolucionNeta, unknown>[] = [
  col.accessor((row) => `${NOMBRES_MESES[row.mes - 1]} ${row.anio}`, {
    id: 'periodo',
    header: 'Período',
    meta: { label: 'Período' },
    enableColumnFilter: false,
  }),
  col.accessor('utilidad_ingresada', {
    header: 'Utilidad Ingresada',
    meta: { label: 'Utilidad Ingresada' },
    cell: (info) => <MontoCRC monto={Number(info.getValue())} />,
    enableColumnFilter: false,
  }),
  col.accessor('devolucion_utilidad', {
    header: 'Devolución Utilidad',
    meta: { label: 'Devolución Utilidad' },
    cell: (info) => <MontoCRC monto={Number(info.getValue())} />,
    enableColumnFilter: false,
  }),
  col.accessor('utilidad_total', {
    header: 'Total Utilidad',
    meta: { label: 'Total Utilidad' },
    cell: (info) => <MontoCRC monto={Number(info.getValue())} />,
    enableColumnFilter: false,
  }),
  col.accessor('utilidad_gastada', {
    header: 'Gastada',
    meta: { label: 'Gastada' },
    cell: (info) => <MontoCRC monto={Number(info.getValue())} />,
    enableColumnFilter: false,
  }),
  col.accessor('utilidad_neta', {
    header: 'Neta',
    meta: { label: 'Neta' },
    cell: (info) => (
      <span className={cn('font-semibold', Number(info.getValue()) < 0 && 'text-destructive')}>
        <MontoCRC monto={Number(info.getValue())} />
      </span>
    ),
    enableColumnFilter: false,
  }),
];

export function UtilidadNetaPantalla() {
  const { periodo, setModo, setAnio, setMes, setTrimestre, setDesde, setHasta } = usePeriodo();
  const ancla = mesAnclaPeriodo(periodo);
  const etiquetaPeriodo = formatearPeriodo(periodo);
  const { data, isLoading, error } = useNeta(periodo);
  const refContenido = useRef<HTMLDivElement>(null);

  const datosEvolucion = data
    ? (() => {
        let acumulado = 0;
        return data.evolucionTemporal.map((f) => {
          acumulado += Number(f.utilidad_neta);
          return {
            periodo: `${MESES_CORTOS[f.mes - 1]} ${String(f.anio).slice(2)}`,
            neta: Number(f.utilidad_neta),
            total: Number(f.utilidad_total),
            gastada: Number(f.utilidad_gastada),
            acumulado,
          };
        });
      })()
    : [];

  return (
    <div ref={refContenido} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Utilidad Neta</h1>
          <p className="mt-1 text-sm text-muted-foreground">{etiquetaPeriodo}</p>
        </div>
        <div data-pdf-ignore className="flex flex-wrap items-start gap-3">
          <SelectorPeriodo
            periodo={periodo}
            onSetModo={setModo}
            onSetAnio={setAnio}
            onSetMes={setMes}
            onSetTrimestre={setTrimestre}
            onSetDesde={setDesde}
            onSetHasta={setHasta}
          />
          <BotonExportar
            titulo="Utilidad Neta"
            subtitulo={etiquetaPeriodo}
            refContenido={refContenido}
            disabled={!data}
            tablas={
              data
                ? [
                    {
                      nombre: 'Evolución',
                      encabezados: [
                        'Año',
                        'Mes',
                        'Utilidad Ingresada (₡)',
                        'Devolución Utilidad (₡)',
                        'Total Utilidad (₡)',
                        'Gastada (₡)',
                        'Neta (₡)',
                      ],
                      filas: data.evolucionTemporal.map((f) => [
                        f.anio,
                        f.mes,
                        Number(f.utilidad_ingresada),
                        Number(f.devolucion_utilidad),
                        Number(f.utilidad_total),
                        Number(f.utilidad_gastada),
                        Number(f.utilidad_neta),
                      ]),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando…
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      {data && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Utilidad Neta por período</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart
                  data={datosEvolucion}
                  margin={{ left: 8, right: 16, top: 8, bottom: 4 }}
                >
                  <XAxis
                    dataKey="periodo"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    interval={2}
                  />
                  <YAxis
                    yAxisId="mes"
                    tickFormatter={abreviarCRC}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="acumulado"
                    orientation="right"
                    tickFormatter={abreviarCRC}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(v, nombre) => [
                      abreviarCRC(Number(v)),
                      nombre === 'acumulado' ? 'Neta Acumulada' : 'Utilidad Neta',
                    ]}
                  />
                  <Legend
                    formatter={(nombre) =>
                      nombre === 'acumulado' ? 'Neta Acumulada' : 'Utilidad Neta'
                    }
                  />
                  <ReferenceLine
                    yAxisId="mes"
                    y={0}
                    stroke="hsl(var(--border))"
                    strokeWidth={1.5}
                  />
                  <Bar yAxisId="mes" dataKey="neta" radius={[4, 4, 0, 0]}>
                    {datosEvolucion.map((fila) => (
                      <Cell key={fila.periodo} fill={fila.neta < 0 ? '#dc2626' : '#2563eb'} />
                    ))}
                  </Bar>
                  <Line
                    yAxisId="acumulado"
                    type="monotone"
                    dataKey="acumulado"
                    stroke="#16a34a"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Detalle por período</CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              <DataGrid
                data={data.evolucionTemporal}
                columns={COLUMNAS}
                pageSize={24}
                showFilters={false}
              />
            </CardContent>
          </Card>
        </>
      )}

      <ComentarioSeccion anio={ancla.anio} mes={ancla.mes} scope="seccion" seccionId="neta" />
    </div>
  );
}
